import {isUIService} from '@github-ui/runtime-environment'
import {useEffect} from 'react'

const hiddenTimePeriodOnScrollInMs = 1000

/**
 * Clones the appropriate clipboard-copy template and appends a copy button to a
 * single snippet element. Mirrors `insertSnippetClipboardCopyButton` from the global
 * `app/assets/modules/github/behaviors/snippet-clipboard-copy.ts` behavior.
 *
 * The `#snippet-clipboard-copy-button` / `#snippet-clipboard-copy-button-unpositioned`
 * templates are rendered into the page by the dotcom layout
 * (`app/views/site/_snippet_clipboard_copy_template.html.erb`), so they are queried
 * from the document at insertion time rather than recreated here.
 */
function insertSnippetClipboardCopyButton(el: HTMLElement, signal: AbortSignal): void {
  const clipboardContent = el.getAttribute('data-snippet-clipboard-copy-content')
  if (clipboardContent === null) return

  // Remove this attribute so that we don't add a second button.
  el.removeAttribute('data-snippet-clipboard-copy-content')

  const useUnpositioned = !!el.closest('.js-snippet-clipboard-copy-unpositioned')
  const template = useUnpositioned
    ? document.querySelector<HTMLTemplateElement>('#snippet-clipboard-copy-button-unpositioned')
    : document.querySelector<HTMLTemplateElement>('#snippet-clipboard-copy-button')

  if (!(template instanceof HTMLTemplateElement)) return

  const documentFragment = template.content.cloneNode(true) as DocumentFragment

  const clonedZeroClipboardContainer = documentFragment.children[0]
  if (!(clonedZeroClipboardContainer instanceof HTMLElement)) return

  const clipboardCopyElement = clonedZeroClipboardContainer.children[0]
  if (!(clipboardCopyElement instanceof HTMLElement)) return

  clipboardCopyElement.setAttribute('value', clipboardContent)

  if (!useUnpositioned) {
    document.addEventListener(
      'selectionchange',
      () => {
        const selection = document.getSelection()
        if (selection && el.contains(selection.anchorNode)) {
          const selectedText = selection.toString()
          clipboardCopyElement.style.display = selectedText.trim() === '' ? 'inherit' : 'none'
        }
      },
      {signal},
    )

    const scrollableElement = el.querySelector('pre')
    if (scrollableElement !== null) {
      let timer: ReturnType<typeof setTimeout> | undefined

      /* eslint-disable-next-line github/prefer-observers */
      scrollableElement.addEventListener(
        'scroll',
        () => {
          if (timer) clearTimeout(timer)

          clipboardCopyElement.style.display = 'none'
          timer = setTimeout(() => {
            clipboardCopyElement.style.display = 'inherit'
          }, hiddenTimePeriodOnScrollInMs)
        },
        {signal},
      )
    }
  }

  el.appendChild(clonedZeroClipboardContainer)
}

/**
 * Adds copy-to-clipboard buttons to markdown code snippets within the UI Service.
 *
 * In dotcom this is handled by the global `snippet-clipboard-copy.ts` behavior via
 * selector-observer. In ui-service that behavior is not loaded, so this hook provides
 * the equivalent functionality scoped to the markdown container: cloning the
 * server-rendered clipboard-copy templates into each `[data-snippet-clipboard-copy-content]`
 * snippet.
 *
 * This hook is a no-op outside the UI Service to avoid conflicting with the global behavior.
 */
export function useSnippetClipboardCopy(
  containerRef: React.RefObject<HTMLDivElement | null>,
  contentVersion?: unknown,
) {
  useEffect(() => {
    if (!isUIService) return

    const container = containerRef.current
    if (!container) return

    // Track every controller we create so their `selectionchange`/`scroll` listeners
    // can be torn down together when the container unmounts or the content changes.
    const abortControllers = new Set<AbortController>()

    const enrichContainer = () => {
      for (const el of container.querySelectorAll<HTMLElement>('[data-snippet-clipboard-copy-content]')) {
        const abortController = new AbortController()
        abortControllers.add(abortController)
        insertSnippetClipboardCopyButton(el, abortController.signal)
      }
    }

    enrichContainer()

    // Soft-nav may update rich text asynchronously. Observe the container so newly
    // inserted snippets still receive a clipboard copy button. Already-enriched snippets
    // have their `data-snippet-clipboard-copy-content` attribute removed, so re-running
    // enrichment after our own DOM insertions is a no-op (no infinite loop).
    const observer = new MutationObserver(() => {
      enrichContainer()
    })
    observer.observe(container, {childList: true, subtree: true})

    return () => {
      observer.disconnect()
      for (const abortController of abortControllers) {
        abortController.abort()
      }
      abortControllers.clear()
    }
  }, [containerRef, contentVersion])
}
